import React from "react";
import { RefreshCw, ShieldCheck } from "lucide-react";
import Card from "../common/Card";
import Badge from "../common/Badge";
import SectionLabel from "../common/SectionLabel";
import { fmtUsdc } from "./format";

/**
 * StatusPanel — live engine state, rendered verbatim from GET /status:
 * delegation, collateral, automation enabled, open positions. (Signal/trade
 * history lives in the Performance section's "Recent activity", fed by the
 * durable /report.recentEvents — /status.recentSignals is in-memory and
 * resets on deploy, so it is deliberately not rendered here.)
 * This panel invents nothing; when the backend is unreachable it shows an
 * honest empty state instead of mock data.
 */

const Stat = ({ label, value, good }) => (
  <div className="rounded-arc-ctl bg-arc-paper-2 ring-1 ring-arc-line p-3">
    <SectionLabel className="mb-1">{label}</SectionLabel>
    <p className={`text-sm font-semibold ${good ? "text-arc-pos" : "text-arc-ink"}`}>{value}</p>
  </div>
);

const StatusPanel = ({ status, onRefresh, refreshing, backendReady }) => (
  <Card hover={false} as="section" aria-label="Automation status">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <ShieldCheck size={17} className="text-arc-accent-ink" aria-hidden="true" />
        <h3 className="text-h2 text-arc-ink">Status</h3>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing || !backendReady}
        className="inline-flex items-center gap-1.5 text-caption text-arc-ink-2 rounded-arc-sm px-2.5 py-1.5 ring-1 ring-arc-line hover:bg-arc-paper-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} aria-hidden="true" />
        Refresh
      </button>
    </div>

    <div className="grid sm:grid-cols-3 gap-3 mb-4">
      <Stat
        label="Delegation"
        value={status?.delegationOk ? "Active" : "Not active"}
        good={status?.delegationOk}
      />
      <Stat label="Collateral" value={status ? fmtUsdc(status.collateralUsdc) : "—"} />
      <Stat
        label="Automation"
        value={status?.enabled ? "Enabled" : "Disabled"}
        good={status?.enabled}
      />
    </div>

    <div>
      <SectionLabel className="mb-2">Open positions</SectionLabel>
      {status?.openPositions?.length ? (
        <div className="space-y-2">
          {status.openPositions.map((p, i) => (
            <div
              key={`${p.pair}-${i}`}
              className="flex items-center justify-between rounded-arc-ctl ring-1 ring-arc-line p-2.5 text-xs"
            >
              <span className="tnum text-arc-ink flex items-center gap-2">
                <Badge tone={p.long ? "pos" : "neg"}>{p.long ? "LONG" : "SHORT"}</Badge>
                {p.pair}
              </span>
              <span className="tnum text-arc-ink-2">
                Size {fmtUsdc(p.sizeUsd)} · Collateral {fmtUsdc(p.collateralUsd)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-caption text-arc-ink-3">No open positions.</p>
      )}
    </div>

  </Card>
);

export default StatusPanel;
